import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("access_token")}` });

/** eBay Store Newsletter: pick up to 10 eBay-listed articles and send an ITEM_SHOWCASE campaign. */
export const EbayNewsletterWidget = () => {
  const [data, setData] = useState({ eligible: [], history: [], max_items: 10 });
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("🆕 Nuovi arrivi da Edicola Cerasuolo");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [report, setReport] = useState(null);

  const loadReport = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ebay/newsletter/report`, { headers: authHeaders() });
      setReport(r.data);
    } catch (e) {
      setReport({ report_error: e.response?.data?.detail || e.message });
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ebay/newsletter`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      console.warn("[ebay-newsletter] load failed:", e?.message);
    }
  }, []);
  useEffect(() => { load(); loadReport(); }, [load, loadReport]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < data.max_items ? [...s, id] : s));

  const send = async () => {
    if (!selected.length || busy) return;
    if (!window.confirm(`Inviare la newsletter eBay con ${selected.length} prodotti a tutti gli iscritti e follower del negozio?`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/ebay/newsletter`, { article_ids: selected, subject, message }, { headers: authHeaders() });
      setResult({ ok: true, text: `✅ Newsletter eBay programmata (id ${r.data.ebay_campaign_id || "—"}) — eBay la recapita ai suoi iscritti tra circa 10 minuti.` });
      setSelected([]);
      await load();
    } catch (e) {
      setResult({ ok: false, text: `❌ ${e.response?.data?.detail || "Errore"}` });
    } finally {
      setBusy(false);
    }
  };

  const runWeekly = async () => {
    if (busy || !window.confirm("Eseguire ora la newsletter automatica dei nuovi arrivi eBay di questa settimana?")) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/ebay/newsletter/run-weekly`, {}, { headers: authHeaders() });
      setResult({ ok: true, text: r.data.sent ? "✅ Newsletter settimanale inviata" : r.data.reason === "already_sent" ? "ℹ️ Già inviata questa settimana" : "ℹ️ Nessun nuovo articolo eBay negli ultimi 7 giorni: nessun invio" });
      await load();
    } catch (e) {
      setResult({ ok: false, text: `❌ ${e.response?.data?.detail || "Errore"}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="ebay-newsletter-widget">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2"><Mail className="w-5 h-5 text-sky-400" /> Newsletter del Negozio eBay</CardTitle>
        <p className="text-xs text-gray-400">
          Ogni lunedì alle 09:15 parte da sola con i nuovi articoli eBay della settimana (solo se ce ne sono) e ogni promozione con banner creata sul sito genera una newsletter "Saldi". Qui puoi inviarne una subito scegliendo fino a {data.max_items} prodotti. Destinatari: iscritti alla newsletter + follower del negozio (gestiti da eBay).
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-2">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={60} placeholder="Oggetto"
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded-lg px-3 py-2" data-testid="ebay-newsletter-subject" />
          <input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={900} placeholder="Messaggio (opzionale)"
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded-lg px-3 py-2" data-testid="ebay-newsletter-message" />
        </div>
        {data.eligible.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="ebay-newsletter-empty">Nessun articolo con inserzione eBay attiva e disponibile.</p>
        ) : (
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1" data-testid="ebay-newsletter-list">
            {data.eligible.map((a) => (
              <label key={a.id} className={`flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 cursor-pointer ${selected.includes(a.id) ? "bg-sky-900/40 text-white" : "text-gray-300 hover:bg-gray-700/50"}`} data-testid={`ebay-newsletter-item-${a.id}`}>
                <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} />
                <span className="truncate flex-1">{a.title}</span>
                <span className="text-[10px] text-gray-500 font-mono">#{a.ebay?.listing_id}</span>
              </label>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={send} disabled={busy || !selected.length} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="ebay-newsletter-send-btn">
            {busy ? "Invio…" : `📧 Invia newsletter eBay (${selected.length}/${data.max_items})`}
          </Button>
          <Button onClick={runWeekly} disabled={busy} variant="outline" className="border-gray-600 text-gray-200 hover:bg-gray-700" data-testid="ebay-newsletter-weekly-btn">
            🔄 Esegui ora la settimanale
          </Button>
          {result && <span className={`text-xs font-semibold ${result.ok ? "text-green-300" : "text-red-300"}`} data-testid="ebay-newsletter-result">{result.text}</span>}
        </div>
        {report && (
          <div className="border-t border-gray-700 pt-3" data-testid="ebay-newsletter-report">
            <p className="text-xs font-semibold text-gray-300 mb-2">📊 Risultati (dati eBay, ultimi 12 mesi)</p>
            {report.report ? (
              <div className="grid grid-cols-4 gap-2">
                {[["Email inviate", report.report.emailsSent ?? report.report.totalEmailSent ?? report.report.sentCount], ["Aperture", report.report.openCount ?? report.report.totalOpenCount], ["Click", report.report.clickCount ?? report.report.totalClickCount], ["Vendite", report.report.totalSales ?? report.report.salesCount]].map(([l, v]) => (
                  <div key={l} className="bg-gray-900 rounded-lg py-2 text-center" data-testid={`ebay-report-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="text-lg font-extrabold text-sky-300">{v ?? "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">{l}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500" data-testid="ebay-report-unavailable">Aperture e click non ancora disponibili da eBay (compaiono dopo i primi invii). {report.report_error ? "" : ""}</p>
            )}
          </div>
        )}
        {data.history.length > 0 && (
          <ul className="border-t border-gray-700 pt-3 space-y-1" data-testid="ebay-newsletter-history">
            {data.history.slice(0, 6).map((h) => {
              const st = report?.statuses?.[h.ebay_campaign_id];
              return (
                <li key={h.id} className="text-xs text-gray-400 flex gap-2 items-center">
                  <span className="whitespace-nowrap">{new Date(h.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
                  <span className="text-gray-200 truncate flex-1">{h.subject}</span>
                  <span className="whitespace-nowrap">{h.items?.length} prodotti · {h.source === "weekly_auto" ? "automatica" : h.source === "promo_auto" ? "promo" : "manuale"}</span>
                  {st && <span className={`px-1.5 py-0.5 rounded font-bold ${st.emailCampaignStatus === "SENT" ? "bg-green-900/50 text-green-300" : "bg-gray-700 text-gray-200"}`} data-testid={`ebay-campaign-status-${h.id}`}>{st.emailCampaignStatus}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
